import { CommandTrackingView } from "@/types/command-tracking.type";
import { AreaStateAggregateType } from "@/routes/area-aggregate/types/area-state-aggregate.type";
import tw from "twin.macro";
import ProgressionStatusCard from "../progression-status-card";
import ProgressChrono from "../progress-chrono";
import { useCommandTimeProgression } from "@/routes/area-aggregate/hooks/use-command-time-progression";
import ModulesSumByState from "../modules-sum-by-state";
import { useTranslation } from "react-i18next";
import StepActionButton from "../step-action-button";
import { getModuleImageForCommand } from "../utils/get-module-image-for-command";
import { SwitchAnimatedArrowRender } from "../switchs/switch-animated-arrow-render";
import { ModuleCommandEnum } from "@/types/enums/module-command.enum";
import EmergencyStopHoldButton from "../emergency-stop-hold-button";
import EmergencyStopSupportMessage from "../emergency-stop-support-message";
import { isEmergencyStopActive } from "../utils/is-emergency-stop";
import { getAchivedStateForCommand } from "../utils/get-achived-state-for-command.ts";
import { getNextStepAction } from "../utils/get-next-step-action.ts";
import { getNextStepTranslation } from "../utils/get-next-step-translation";
import { getStateToDisplayForCommand } from "../utils/get-state-to-display-for-command";
import { computeModulesSumByState } from "../utils/compute-modules-sum-by-state.tsx";
import { getArrowForAction } from "../utils/get-arrow-for-action.ts";
import { SheetsType } from "../types/sheets.type.ts";
import { SheetsEnum } from "../enums/sheets.enum.ts";
import { toast } from "sonner";
import { CommandToSend, useSendCommand } from "@/api/areas/mutations";
import { useAreaDataProvider } from "@/routes/area-aggregate/components/area-data-loader";

function ActionInProgressSheetBody({
  pushSheet,
  areaStateAggregate,
  areaCommandTracking,
  context,
}: {
  areaStateAggregate: AreaStateAggregateType;
  areaCommandTracking: CommandTrackingView;
  context: { currentAction: ModuleCommandEnum };
  pushSheet: (view: SheetsType) => void;
}) {
  const { mutateAsync: sendCommand, isPending } = useSendCommand({
    areaId: areaStateAggregate.areaId,
  });
  const { applyOptimisticCommand } = useAreaDataProvider();
  const { t: translator } = useTranslation();
  const { isOutOfTimeChrono, progressPercent } = useCommandTimeProgression();

  const { numberOfModulesAchieved, percentageOfModulesAchieved, isCompleted } =
    computeModulesSumByState(areaStateAggregate, context.currentAction);

  // Si l'arrêt d'urgence est confirmé (gateway), on n'affiche plus que le
  // message support — même au ré-ouverture de ce sheet.
  if (isEmergencyStopActive(areaCommandTracking)) {
    return (
      <div tw="flex flex-col items-stretch gap-4 p-4 pt-0">
        <EmergencyStopSupportMessage />
      </div>
    );
  }

  return (
    <div tw="flex flex-col items-stretch gap-4 p-4 pt-0">
      {!isOutOfTimeChrono && (
        <ProgressionStatusCard currentAction={context.currentAction}>
          <ProgressChrono progressPercent={progressPercent} />{" "}
        </ProgressionStatusCard>
      )}

      <div
        css={[
          tw`flex items-center justify-center bg-[rgb(230, 245, 233)] rounded-xl p-2 w-full h-60 relative`,
          isOutOfTimeChrono && tw`hidden`,
        ]}
      >
        <img
          src={getModuleImageForCommand(context.currentAction)}
          alt="Pantograph"
          css={[tw`w-60 h-48`]}
        />{" "}
        <div
          css={[
            tw`absolute inset-0 flex items-center justify-center pointer-events-none`,
          ]}
        >
          <SwitchAnimatedArrowRender command={context.currentAction} />
        </div>
      </div>

      {isOutOfTimeChrono && (
        <ModulesSumByState
          numberOfModulesAchieved={numberOfModulesAchieved}
          percentageOfModulesAchieved={percentageOfModulesAchieved}
          isCompleted={isCompleted}
          numberOfModulesInArea={areaStateAggregate.numberOfModulesInArea}
          canopeesTranslation={translator("canopies")}
          achievedStateTranslation={getAchivedStateForCommand(
            context.currentAction,
            translator
          )}
          stateToDisplay={getStateToDisplayForCommand(context.currentAction)}
          onClick={() => {
            pushSheet({
              id: SheetsEnum.MODULES_LIST,
              firstOfList: getStateToDisplayForCommand(context.currentAction),
              canGoBack: true,
            });
          }}
        />
      )}

      <div tw="pt-10 w-full gap-4 flex flex-col justify-center">
        <EmergencyStopHoldButton
          disabled={isPending}
          label={translator("action.emergencyStop")}
          onComplete={() => {
            try {
              applyOptimisticCommand(ModuleCommandEnum.PAUSE);
              sendCommand({ command: ModuleCommandEnum.PAUSE });
              pushSheet({
                id: SheetsEnum.EMERGENCY_STOP,
                canGoBack: false,
              });
            } catch (error) {
              toast.error("Erreur lors de l'arrêt d'urgence");
            }
          }}
        />
        <StepActionButton
          disabled={!isOutOfTimeChrono || isPending}
          actionTranslation={getNextStepTranslation(
            context.currentAction,
            translator
          )}
          arrowDirection={getArrowForAction(
            getNextStepAction(context.currentAction)
          )}
          onClick={() => {
            try {
              const nextCommand = getNextStepAction(
                context.currentAction
              ) as CommandToSend;
              applyOptimisticCommand(nextCommand);
              sendCommand({ command: nextCommand });
              pushSheet({
                id: SheetsEnum.ACTION_IN_PROGRESS,
                context: {
                  currentAction: getNextStepAction(context.currentAction),
                },
                canGoBack: false,
              });
            } catch (error) {
              toast.error("Erreur lors de la montée");
            }
          }}
        />
      </div>
    </div>
  );
}

export default ActionInProgressSheetBody;
